"use client";

import { FC, useRef, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
interface BFLTab {
  label: string;
  value: string;
}

interface BFLTabsProps {
  tabs: BFLTab[];
  selectedTab: string;
  color?: string;
  onTabChange: (tab: any) => void;
  className?: string;
  tabClassName?: string;
  gapClassName?: string;
  indicatorClassName?: string;
  paragraphClassName?: string;
  tabPaddingClassName?: string;
}

const BFLTabs: FC<BFLTabsProps> = ({
  tabs,
  selectedTab,
  color,
  onTabChange,
  className,
  tabClassName,
  gapClassName,
  indicatorClassName,
  paragraphClassName,
  tabPaddingClassName,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const MOBILE_BREAKPOINT = 768;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    
    // Cleanup listener on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleClick = (index: number, value: string) => {
    const container = containerRef.current;
    if (!container) return;
    
    // Only scroll into view on mobile viewport
    if (isMobile) {
      const item = container.children[index] as HTMLElement;
      if (item && typeof item.scrollIntoView === 'function') {
        item.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    }
    
    onTabChange(value);
  };

  return (
    <div
      className={twMerge(
        "no-scrollbar w-full overflow-x-scroll border-b border-gray-300",
        className,
      )}
    >
      <div
        ref={containerRef}
        className={twMerge(
          "flex min-h-[26.22px] items-center text-[12px] leading-[14px] md:h-auto md:text-base md:leading-normal",
          gapClassName,
        )}
      >
        {tabs.map((tab, index) => {
          const isActive = selectedTab === tab.value;

          return (
            <button
              key={tab.value}
              onClick={() => handleClick(index, tab.value)}
              className={twMerge(
                "relative cursor-pointer whitespace-nowrap",
                tabPaddingClassName,
                tabClassName,
              )}
            >
              <p
                className={twMerge(
                  isActive ? "text-[#ff6700]" : (color ?? "text-[#0c2340]"),
                  paragraphClassName,
                )}
              >
                {tab.label}
              </p>
              {isActive && (
                <span
                  className={twMerge(
                    "absolute -bottom-[5px] left-0 h-[2px] w-full rounded-full bg-[#ff6a00] md:bottom-[-14px] md:h-[3px]",
                    indicatorClassName,
                  )}
                ></span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BFLTabs;
